/**
 * Application logging configuration
 * Provides structured logging with different levels and contexts
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: unknown;
}

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: LogContext;
}

class Logger {
  private static instance: Logger;
  private environment: string;

  private constructor() {
    this.environment = process.env.NEXT_PUBLIC_APP_ENV || 'development';
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private formatLog(
    level: LogLevel,
    message: string,
    context?: LogContext
  ): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      context: {
        environment: this.environment,
        ...context,
      },
    };
  }

  private output(entry: LogEntry): void {
    if (this.environment === 'development') {
      const color = {
        debug: '\x1b[34m', // blue
        info: '\x1b[32m', // green
        warn: '\x1b[33m', // yellow
        error: '\x1b[31m', // red
      }[entry.level];

      console.log(
        `${color}[${entry.level.toUpperCase()}]\x1b[0m`,
        entry.timestamp,
        entry.message,
        entry.context || ''
      );
    } else {
      // In production, send to logging service
      // Example: sendToLoggingService(entry);
    }
  }

  debug(message: string, context?: LogContext): void {
    this.output(this.formatLog('debug', message, context));
  }

  info(message: string, context?: LogContext): void {
    this.output(this.formatLog('info', message, context));
  }

  warn(message: string, context?: LogContext): void {
    this.output(this.formatLog('warn', message, context));
  }

  error(message: string, error?: Error, context?: LogContext): void {
    this.output(
      this.formatLog('error', message, {
        ...context,
        error: {
          message: error?.message,
          stack: error?.stack,
        },
      })
    );
  }
}

export const logger = Logger.getInstance();
